import { cloneDeep } from 'lodash';
import type { AddLayerObject, Map } from 'maplibre-gl';
import { useCallback, useEffect } from 'react';

import { emptyGeoJSONSource } from '@/constants/constants';
import { ITASCA_URL, PENROSE_URL } from '@/constants/urls';
import { AcresMapboxSourceIds } from '@/mapbox-layers';
import { TileConfig } from '@/models/tileConfig';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';
import createGenericSource, {
  GenericSourceOptions,
} from '@/utils/add-source/createGenericSource';
import { addLayer, addSource } from '@/utils/asyncMapbox';
import Logger from '@/utils/logger';

import { useTileConfig } from '../useTileConfig';
import { sourceConfig, SourceType } from './config/sourceConfig';

const handleTileMapping = (key: keyof SourceType): keyof SourceType => {
  // We want a different source layer for the county max zoom (but still want to use the same tileset)
  return key;
};

const useActiveSource = (map: Map | undefined, layers: AddLayerObject[]) => {
  const { tileConfig } = useTileConfig();
  const { setAreBaseSourcesLoaded, isMapInstanceLoaded } = useMapboxContext();

  const handleLayers = useCallback(async () => {
    if (!map) return;

    await addSource(map, AcresMapboxSourceIds.CORELOGIC_RESULTS, {
      ...emptyGeoJSONSource,
      promoteId: 'gid',
    });

    const layerPromise = layers.map((l) => addLayer(map, l));

    await Promise.allSettled(layerPromise);
  }, [map, layers]);

  const addTileSources = useCallback(
    async (source: SourceType) => {
      if (!map) return;

      const generateSourcePromises = Object.keys(source).map(
        (key: keyof SourceType) => {
          const sourceProperties = source[key];

          // See allMapboxLayers.tsx for the new way of handling these.
          if (
            [
              'states',
              'counties',
              'townships',
              'sections',
              'parcels',
              'clu',
              'parcel-centroids',
            ].includes(key)
          )
            return undefined;

          if (sourceProperties !== null) {
            const options: GenericSourceOptions = {
              ...sourceProperties,
              sourceId: key,
            };

            const shouldAppendPBF =
              !options.url?.includes(ITASCA_URL) &&
              !options.url?.includes(PENROSE_URL);

            return createGenericSource(map, options, shouldAppendPBF);
          }

          return undefined;
        }
      );

      await Promise.allSettled(generateSourcePromises);
      setAreBaseSourcesLoaded(true);
      await handleLayers();
    },
    [map, handleLayers, setAreBaseSourcesLoaded]
  );

  /** Use the tile config to assign tile URLs */
  const assignSourceUrls = useCallback(() => {
    if (!tileConfig || !map) return;

    const source = sourceConfig;

    const sourceConfigCopy: SourceType = cloneDeep(source);

    try {
      // All new sources must have same name as config url
      Object.keys(sourceConfigCopy).forEach((key: keyof TileConfig) => {
        const currSourceConfig = sourceConfigCopy[key];
        const currTileConfigSource = tileConfig[handleTileMapping(key)];

        if (currSourceConfig !== null) {
          // This cast is here because of CropHistory, teh value in tile config is a Record<string, string>
          //  but this `url` only accepts a string.  CropHistory is `null` in currSourceConfig so it is skipped
          currSourceConfig.url = currTileConfigSource as string;
        }
      });
    } catch (err) {
      Logger.error(err);
    }

    addTileSources(sourceConfigCopy);
  }, [tileConfig, map, addTileSources]);

  const loadSource = useCallback(async () => {
    if (!map || !tileConfig || !isMapInstanceLoaded) return;

    map.once('dataloading', assignSourceUrls);
  }, [map, tileConfig, isMapInstanceLoaded, assignSourceUrls]);

  useEffect(() => {
    loadSource();
  }, [loadSource]);
};

export default useActiveSource;
